"use client"

import * as React from "react"
import { Link, useNavigate } from "react-router"
import { cn } from "@/lib/utils"
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import { utilityBarActionButtonClass } from "@/components/utility-bar-chrome"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { FILTER_LIST_SEARCH_THRESHOLD } from "@exxatdesignux/ui/lib/filter-list-search-threshold"
// The page `<h1>` type, shared with `PageHeader` so a record switcher title and a
// plain title are the same size on sibling routes.
import { PAGE_TITLE_TYPE_CLASS } from "@/components/page-header"
import { useScrollStuck } from "@/hooks/use-scroll-stuck"

export interface PageBreadcrumbMenuOption {
  id: string
  label: string
  href?: string
  onSelect?: () => void
  selected?: boolean
  /** Leading content (avatar, icon). Optional. */
  start?: React.ReactNode
  /** Trailing content (status chip). Optional. */
  end?: React.ReactNode
}

export interface PageBreadcrumbTrailItem {
  label: string
  href?: string
  /** Peer options — renders a switcher instead of a static link or label. */
  menu?: PageBreadcrumbMenuOption[]
  menuAriaLabel?: string
}

export interface PageBreadcrumbBackProps {
  /** Destination label (e.g. "Question hub") — shown after the back icon. */
  label: string
  href: string
  /** Current page title — shown beside the back icon when scrolled. */
  scrollTitle?: string
  scrollTitleMenu?: PageBreadcrumbMenuOption[]
  scrollTitleMenuAriaLabel?: string
  className?: string
}

export interface PageBreadcrumbTrailProps {
  /** Linkable ancestors (e.g. Question hub). */
  items?: PageBreadcrumbTrailItem[]
  /**
   * Final segment in the trail. Omit when the current page is the `PageHeader`
   * `<h1>` — use ancestors-only above the title (no duplicate label).
   */
  currentPage?: string
  /** Switch among peer records on the current page segment (detail routes). */
  currentPageMenu?: PageBreadcrumbMenuOption[]
  currentPageMenuAriaLabel?: string
  /**
   * `header` — SiteHeader: ancestors + `currentPage` on one line.
   * `content` — ancestors only, above `PageHeader` title.
   */
  variant?: "header" | "content"
  className?: string
}

function BreadcrumbMenuOptionMarker({ selected }: { selected?: boolean }) {
  return selected ? (
    <i className="fa-solid fa-check size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
  ) : (
    <span className="size-4 shrink-0" aria-hidden="true" />
  )
}

function RecordMenuTriggerButton({
  label,
  menuAriaLabel,
  isCurrentPage,
  variant = "breadcrumb",
  ...props
}: React.ComponentProps<"button"> & {
  label: string
  menuAriaLabel?: string
  isCurrentPage?: boolean
  variant?: "breadcrumb" | "title"
}) {
  return (
    <button
      type="button"
      aria-current={isCurrentPage ? "page" : undefined}
      aria-label={menuAriaLabel ?? `Switch ${label}`}
      aria-haspopup="menu"
      className={cn(
        variant === "title"
          ? "inline-flex max-w-full min-w-0 items-center gap-2.5 rounded-md py-0.5 text-left font-inherit text-inherit outline-none hover:text-interactive-hover-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
          : cn(
              "inline-flex h-8 max-w-full min-w-0 items-center gap-1 rounded-md px-0.5 font-sans text-sm leading-5 outline-none",
              "hover:text-interactive-hover-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
              isCurrentPage ? "font-medium text-foreground" : "text-muted-foreground",
            ),
      )}
      {...props}
    >
      <span className={variant === "title" ? "min-w-0 text-left" : "min-w-0 truncate"}>{label}</span>
      <i
        className={cn(
          "fa-light fa-chevron-down shrink-0 opacity-70",
          variant === "title" ? "ms-0.5 text-sm" : "text-xs",
        )}
        aria-hidden="true"
      />
    </button>
  )
}

function RecordMenuSegment({
  label,
  menu,
  menuAriaLabel,
  isCurrentPage = false,
  variant = "breadcrumb",
}: {
  label: string
  menu: PageBreadcrumbMenuOption[]
  menuAriaLabel?: string
  isCurrentPage?: boolean
  variant?: "breadcrumb" | "title"
}) {
  const navigate = useNavigate()
  const searchable = menu.length > FILTER_LIST_SEARCH_THRESHOLD
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")

  React.useEffect(() => {
    if (!open) setSearch("")
  }, [open])

  const filteredMenu = React.useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return menu
    return menu.filter(option => option.label.toLowerCase().includes(query))
  }, [menu, search])

  const selectOption = React.useCallback(
    (option: PageBreadcrumbMenuOption) => {
      if (option.href) navigate(option.href)
      else option.onSelect?.()
      setOpen(false)
    },
    [navigate],
  )

  if (searchable) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <RecordMenuTriggerButton
            label={label}
            menuAriaLabel={menuAriaLabel}
            isCurrentPage={isCurrentPage}
            variant={variant}
          />
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[min(20rem,calc(100vw-2rem))] p-0">
          <div className="border-b border-border px-2 py-1.5">
            <div className="relative">
              <i
                className="fa-light fa-magnifying-glass pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                type="search"
                placeholder="Search…"
                value={search}
                onChange={event => setSearch(event.target.value)}
                className={cn("h-8 ps-7 text-xs", search ? "pe-8" : "pe-2")}
                autoFocus
                aria-label={menuAriaLabel ?? `Search ${label}`}
              />
              {search ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch("")}
                  className="icon-button-chrome absolute end-1.5 top-1/2 inline-flex size-8 -translate-y-1/2 items-center justify-center rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                >
                  <i className="fa-light fa-xmark text-xs" aria-hidden="true" />
                </button>
              ) : null}
            </div>
          </div>
          <div
            role="menu"
            aria-label={menuAriaLabel ?? `Switch ${label}`}
            className="max-h-[min(24rem,70vh)] overflow-y-auto py-1"
          >
            {filteredMenu.length === 0 ? (
              <p className="px-3 py-2 text-xs text-muted-foreground">No matches.</p>
            ) : (
              filteredMenu.map(option => (
                <button
                  key={option.id}
                  type="button"
                  role="menuitem"
                  onClick={() => selectOption(option)}
                  className="flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-start text-sm transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:outline-none"
                >
                  <BreadcrumbMenuOptionMarker selected={option.selected} />
                  {option.start ? (
                    <span className="shrink-0">{option.start}</span>
                  ) : null}
                  <span className="min-w-0 flex-1 truncate">{option.label}</span>
                  {option.end ? (
                    <span className="ms-auto shrink-0">{option.end}</span>
                  ) : null}
                </button>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <RecordMenuTriggerButton
          label={label}
          menuAriaLabel={menuAriaLabel}
          isCurrentPage={isCurrentPage}
          variant={variant}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-[min(24rem,70vh)] w-[min(20rem,calc(100vw-2rem))] overflow-y-auto"
      >
        {menu.map(option => (
          <DropdownMenuItem
            key={option.id}
            className="gap-2"
            onSelect={option.onSelect}
            asChild={option.href != null ? true : undefined}
          >
            {option.href != null ? (
              <Link to={option.href} className="flex min-w-0 flex-1 items-center gap-2">
                <BreadcrumbMenuOptionMarker selected={option.selected} />
                {option.start ? (
                  <span className="shrink-0">{option.start}</span>
                ) : null}
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
                {option.end ? (
                  <span className="ms-auto shrink-0">{option.end}</span>
                ) : null}
              </Link>
            ) : (
              <>
                <BreadcrumbMenuOptionMarker selected={option.selected} />
                {option.start ? (
                  <span className="shrink-0">{option.start}</span>
                ) : null}
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
                {option.end ? (
                  <span className="ms-auto shrink-0">{option.end}</span>
                ) : null}
              </>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** BreadcrumbMenuSegment — peer record switcher for trail segments. */
function BreadcrumbMenuSegment(props: React.ComponentProps<typeof RecordMenuSegment>) {
  return <RecordMenuSegment {...props} />
}

/**
 * Peer record switcher — breadcrumb leaf, utility bar title, or `PageHeader` title.
 */
export function PageRecordMenuSwitcher({
  variant = "breadcrumb",
  isCurrentPage = true,
  ...props
}: React.ComponentProps<typeof RecordMenuSegment>) {
  return <RecordMenuSegment variant={variant} isCurrentPage={isCurrentPage} {...props} />
}

/**
 * Page `<h1>` record switcher — same search/popover menu as breadcrumbs, sized for
 * `PageHeader` titles on detail routes.
 */
export function PageTitleRecordSwitcher(
  props: Omit<React.ComponentProps<typeof RecordMenuSegment>, "variant" | "isCurrentPage">,
) {
  return (
    <h1
      className={cn("line-clamp-2 min-w-0 overflow-hidden break-words", PAGE_TITLE_TYPE_CLASS)}
      suppressHydrationWarning
    >
      <RecordMenuSegment {...props} variant="title" isCurrentPage />
    </h1>
  )
}

function BreadcrumbTrailSegment({
  crumb,
  isCurrentPage = false,
}: {
  crumb: Pick<PageBreadcrumbTrailItem, "label" | "href" | "menu" | "menuAriaLabel">
  isCurrentPage?: boolean
}) {
  if (crumb.menu?.length) {
    return (
      <BreadcrumbMenuSegment
        label={crumb.label}
        menu={crumb.menu}
        menuAriaLabel={crumb.menuAriaLabel}
        isCurrentPage={isCurrentPage}
      />
    )
  }

  if (isCurrentPage) {
    return (
      <BreadcrumbPage className="inline-flex h-8 max-w-full min-w-0 items-center truncate font-sans text-sm font-medium leading-5">
        {crumb.label}
      </BreadcrumbPage>
    )
  }

  if (crumb.href) {
    return (
      <BreadcrumbLink asChild>
        <Link
          to={crumb.href}
          className="inline-flex max-w-[min(100%,10rem)] items-center truncate font-sans text-sm leading-5 text-muted-foreground sm:max-w-[14rem]"
        >
          {crumb.label}
        </Link>
      </BreadcrumbLink>
    )
  }

  return (
    <span className="inline-flex max-w-[min(100%,10rem)] items-center truncate font-sans text-sm leading-5 text-muted-foreground sm:max-w-[14rem]">
      {crumb.label}
    </span>
  )
}

/**
 * Single-step back nav — back icon + parent destination (no chevron trail).
 * Use in `SiteHeader` for focused child routes (composer, wizard) where the
 * page `<h1>` is the current title.
 */
export function PageBreadcrumbBack({
  label,
  href,
  scrollTitle,
  scrollTitleMenu,
  scrollTitleMenuAriaLabel,
  className,
}: PageBreadcrumbBackProps) {
  const isStuck = useScrollStuck()
  const showPageTitle = Boolean(scrollTitle) && isStuck

  return (
    <Breadcrumb className={cn("min-w-0", className)}>
      <BreadcrumbList className="gap-1.5 font-sans tracking-normal">
        <BreadcrumbItem className="min-w-0">
          <BreadcrumbLink asChild>
            <Link
              to={href}
              className="group inline-flex min-w-0 max-w-full items-center gap-1.5 font-sans text-sm text-muted-foreground hover:text-interactive-hover-foreground"
              aria-label={`Back to ${label}`}
            >
              <i
                className="fa-light fa-arrow-left shrink-0 text-xs transition-transform group-hover:-translate-x-0.5"
                aria-hidden="true"
              />
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        {showPageTitle ? (
          <BreadcrumbItem className="min-w-0 flex-1 overflow-hidden">
            {scrollTitleMenu?.length ? (
              <PageRecordMenuSwitcher
                variant="title"
                label={scrollTitle!}
                menu={scrollTitleMenu}
                menuAriaLabel={scrollTitleMenuAriaLabel}
              />
            ) : (
              <BreadcrumbPage
                className={cn(
                  "inline-flex h-8 max-w-full min-w-0 items-center truncate",
                  PAGE_TITLE_TYPE_CLASS,
                )}
              >
                {scrollTitle}
              </BreadcrumbPage>
            )}
          </BreadcrumbItem>
        ) : null}
      </BreadcrumbList>
    </Breadcrumb>
  )
}

/**
 * Product breadcrumb trail — one component for SiteHeader and in-page shells.
 * Uses shadcn `Breadcrumb` primitives with Exxat site-header typography.
 *
 * Header / compact utility bar: every segment keeps its **text label** (never a
 * generic house icon — the first crumb is often a hub like Library, not Home).
 * When the full trail does not fit the slot, middle segments collapse into More
 * (`BreadcrumbEllipsis`); the current page stays a truncating label.
 *
 * Dense utility bar (mobile / reflow): same labels, tighter max width so
 * actions keep room.
 *
 * For back-icon + parent label only, use {@link PageBreadcrumbBack}.
 */
const EMPTY_BREADCRUMB_ITEMS: PageBreadcrumbTrailItem[] = []

/** First + last stay visible; collapse needs a middle segment to hide. */
const BREADCRUMB_COLLAPSE_MIN_SEGMENTS = 3

type TrailSegment = {
  crumb: Pick<PageBreadcrumbTrailItem, "label" | "href" | "menu" | "menuAriaLabel">
  isCurrentPage?: boolean
  key: string
}

function BreadcrumbCollapsedMore({
  segments,
}: {
  segments: TrailSegment[]
}) {
  if (segments.length === 0) return null

  return (
    <BreadcrumbItem className="shrink-0">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className={utilityBarActionButtonClass}
            aria-label="More breadcrumbs"
          >
            <BreadcrumbEllipsis />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-[min(24rem,70vh)] overflow-y-auto">
          <DropdownMenuGroup>
            {segments.map(segment => (
              <DropdownMenuItem
                key={segment.key}
                className="gap-2"
                asChild={segment.crumb.href != null ? true : undefined}
                disabled={
                  segment.isCurrentPage ||
                  (!segment.crumb.href && !segment.crumb.menu?.length)
                }
              >
                {segment.crumb.href != null ? (
                  <Link to={segment.crumb.href}>
                    <span className="min-w-0 truncate">{segment.crumb.label}</span>
                  </Link>
                ) : (
                  <span className="min-w-0 truncate">{segment.crumb.label}</span>
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </BreadcrumbItem>
  )
}

function renderTrailSegments(
  segments: TrailSegment[],
  collapse: boolean,
  measure = false,
) {
  const visible: Array<
    | { type: "segment"; segment: TrailSegment }
    | { type: "more"; segments: TrailSegment[] }
  > = collapse
    ? [
        {
          type: "segment",
          segment: segments[0]!,
        },
        { type: "more", segments: segments.slice(1, -1) },
        { type: "segment", segment: segments[segments.length - 1]! },
      ]
    : segments.map(segment => ({ type: "segment" as const, segment }))

  const entries = visible.filter(
    entry => entry.type !== "more" || entry.segments.length > 0,
  )

  return entries.map((entry, i) => (
    <React.Fragment key={entry.type === "more" ? "breadcrumb-more" : entry.segment.key}>
      {entry.type === "more" ? (
        <BreadcrumbCollapsedMore segments={entry.segments} />
      ) : (
        <BreadcrumbItem
          className={cn(
            "min-w-0",
            measure || !entry.segment.isCurrentPage
              ? "shrink-0 max-w-[min(100%,10rem)] sm:max-w-[14rem]"
              : "min-w-0 flex-1 shrink overflow-hidden",
          )}
        >
          <BreadcrumbTrailSegment
            crumb={entry.segment.crumb}
            isCurrentPage={entry.segment.isCurrentPage}
          />
        </BreadcrumbItem>
      )}
      {i < entries.length - 1 ? (
        <BreadcrumbSeparator className="shrink-0 text-muted-foreground [&>i]:text-xs" />
      ) : null}
    </React.Fragment>
  ))
}

export function PageBreadcrumbTrail({
  items = EMPTY_BREADCRUMB_ITEMS,
  currentPage,
  currentPageMenu,
  currentPageMenuAriaLabel,
  variant = "content",
  className,
}: PageBreadcrumbTrailProps) {
  const isHeader = variant === "header"
  const trailItems = items ?? EMPTY_BREADCRUMB_ITEMS
  const availableRef = React.useRef<HTMLOListElement>(null)
  const measureRef = React.useRef<HTMLOListElement>(null)
  const [overflows, setOverflows] = React.useState(false)

  const segments = React.useMemo<TrailSegment[]>(() => {
    const next: TrailSegment[] = trailItems.map((crumb, i) => ({
      crumb,
      key: `ancestor-${crumb.label}-${i}`,
    }))
    if (currentPage != null) {
      next.push({
        key: `current-${currentPage}`,
        isCurrentPage: true,
        crumb: {
          label: currentPage,
          menu: currentPageMenu,
          menuAriaLabel: currentPageMenuAriaLabel,
        },
      })
    }
    return next
  }, [trailItems, currentPage, currentPageMenu, currentPageMenuAriaLabel])

  const canCollapse = segments.length >= BREADCRUMB_COLLAPSE_MIN_SEGMENTS

  React.useLayoutEffect(() => {
    if (!canCollapse) {
      setOverflows(false)
      return
    }
    const available = availableRef.current
    const measure = measureRef.current
    if (!available || !measure) return

    const update = () => {
      const room = available.clientWidth
      // jsdom / unmounted slot: do not collapse just because width is 0.
      if (room === 0) {
        setOverflows(false)
        return
      }
      setOverflows(measure.scrollWidth > room)
    }

    update()
    if (typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(update)
    observer.observe(available)
    observer.observe(measure)
    return () => observer.disconnect()
  }, [canCollapse, segments])

  return (
    <Breadcrumb
      className={cn(
        "relative flex h-8 min-w-0 max-w-full items-center overflow-hidden",
        className,
      )}
      aria-label="Breadcrumb"
    >
      {canCollapse ? (
        <BreadcrumbList
          ref={measureRef}
          aria-hidden
          className={cn(
            "pointer-events-none invisible absolute inset-y-0 start-0 flex h-8 w-max min-w-0 flex-nowrap items-center gap-1 overflow-visible font-sans tracking-normal",
          )}
        >
          {renderTrailSegments(segments, false, true)}
        </BreadcrumbList>
      ) : null}
      <BreadcrumbList
        ref={availableRef}
        className={cn(
          // h-8 + items-center: leaf and icons share the utility-bar midline.
          "flex h-8 w-full min-w-0 items-center gap-1 overflow-hidden font-sans tracking-normal",
          isHeader && "flex-nowrap",
        )}
      >
        {renderTrailSegments(segments, canCollapse && overflows)}
      </BreadcrumbList>
    </Breadcrumb>
  )
}
